import { doc, getDoc, setDoc } from "firebase/firestore";
import { useCallback, useEffect, useMemo, useState } from "react";
import config from "../config/config.json";
import { firestore } from "../firebase";
import { buildStaticMapUrl } from "../lib/build-static-maps-url";
import { isValidZipCode } from "../lib/is-valid-zip-code";
import { debugLogError } from "../utils/debug-log-error";

const googleGeocodingApiKey = config.apiKeys.googleGeocodingApiKey;
const ZipCodeGeocodeCollection = "zipCodeGeocode";

export function useMapUrl(
  zipCode: string | null,
  latLngCacheKey: string | null = null
) {
  const [mapUrl, setMapUrl] = useState<string | null>(null);

  const cacheLatLng = useCallback(
    (latLng: google.maps.LatLngLiteral) => {
      if (latLngCacheKey) {
        localStorage.setItem(latLngCacheKey, JSON.stringify(latLng));
      }
    },
    [latLngCacheKey]
  );

  useEffect(() => {
    async function fetchMapUrl(currentZipCode: string | null) {
      if (!currentZipCode || !isValidZipCode(currentZipCode)) {
        setMapUrl(null);
        return;
      }

      try {
        // See if we have a cached geocode for this zip code:
        const geocodeDocRef = doc(
          firestore,
          ZipCodeGeocodeCollection,
          currentZipCode
        );
        const geocodeDoc = await getDoc(geocodeDocRef);
        if (geocodeDoc.exists()) {
          const geocodeData = geocodeDoc.data();
          const { lat, lng } = geocodeData.location;
          cacheLatLng({ lat, lng });
          setMapUrl(buildStaticMapUrl(lat, lng));
        } else {
          const geocodeResponse = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?address=${currentZipCode}&key=${googleGeocodingApiKey}`
          );
          const geocodeData = await geocodeResponse.json();

          if (geocodeData.results && geocodeData.results.length > 0) {
            const { lat, lng } = geocodeData.results[0].geometry.location;
            cacheLatLng({ lat, lng });
            await setDoc(geocodeDocRef, {
              location: { lat, lng },
              zipCode: currentZipCode,
            });
            setMapUrl(buildStaticMapUrl(lat, lng));
          } else {
            debugLogError(
              "useMapUrl",
              `No geocoding results found for zip code: ${currentZipCode}`
            );
            setMapUrl(null);
          }
        }
      } catch (error) {
        debugLogError("useMapUrl", error);
        setMapUrl(null);
      }
    }

    fetchMapUrl(zipCode);
  }, [zipCode, cacheLatLng]);

  return useMemo(() => ({ mapUrl }), [mapUrl]);
}
